import type API from "@/Api/API";
import type { SubscriptionLimits } from "@/types/subscription";
import type { User, UserDto, UserGroup, UserGroupDto } from "@/types/user";

export default class Users {
	api: API;

	constructor(api: API) {
		this.api = api;
	}

	async getUsers(): Promise<User[]> {
		return this.api.processRequest("GET", "/users").then((res) => res.data);
	}

	async addUser(user: UserDto): Promise<User> {
		return this.api
			.processRequest("POST", "/auth/register", {
				data: user,
			})
			.then((res) => res.data);
	}

	async updateUser(uuid: string, dto: UserDto): Promise<User> {
		return this.api
			.processRequest("PUT", `/users/${uuid}`, {
				data: dto,
			})
			.then((res) => res.data);
	}

	async deleteUser(uuid: string): Promise<void> {
		await this.api.processRequest("DELETE", `/users/${uuid}`);
	}

	async getUserGroups(): Promise<UserGroup[]> {
		return this.api
			.processRequest("GET", "/groups")
			.then((res) => res.data);
	}

	async getMyGroups(): Promise<UserGroup[]> {
		return this.api
			.processRequest("GET", "/groups/me")
			.then((res) => res.data);
	}

	async getUserGroup(uuid: string): Promise<UserGroup> {
		return this.api
			.processRequest("GET", `/groups/${uuid}`)
			.then((res) => res.data);
	}

	async addUserGroup(group: UserGroupDto): Promise<UserGroup> {
		return this.api
			.processRequest("POST", "/groups", {
				data: group,
			})
			.then((res) => res.data);
	}

	async updateUserGroup(
		uuid: string,
		group: UserGroupDto
	): Promise<UserGroup> {
		return this.api
			.processRequest("PUT", `/groups/${uuid}`, {
				data: group,
			})
			.then((res) => res.data);
	}

	async deleteUserGroup(uuid: string): Promise<void> {
		await this.api.processRequest("DELETE", `/groups/${uuid}`);
	}

	async editGroupUsers(uuid: string, users: User[]): Promise<void> {
		return this.api
			.processRequest("PUT", `/groups/${uuid}/users`, {
				data: { users: users.map((user) => user.uuid) },
			})
			.then((res) => res.data);
	}

	async getUsersAndGroups(): Promise<{
		users: { uuid: string; name: string }[];
		groups: { uuid: string; name: string }[];
	}> {
		return this.api
			.processRequest("GET", "/users/all")
			.then((res) => res.data);
	}

	async getAllUsersDashboardStats(): Promise<
		Record<
			string,
			{
				totalScenes: number;
				totalMusics: number;
				totalPlaylists: number;
				totalSessions: number;
			}
		>
	> {
		return this.api
			.processRequest("GET", `/users/dashboard-stats`)
			.then((res) => res.data);
	}

	async getMyLimits(): Promise<SubscriptionLimits> {
		return this.api
			.processRequest("GET", `/auth/me/limits`)
			.then((res) => res.data);
	}

	async updateGroupOwners(
		groupUuid: string,
		owners: string[]
	): Promise<UserGroup> {
		return this.api
			.processRequest("PUT", `/groups/${groupUuid}/owners`, {
				data: { owners },
			})
			.then((res) => res.data);
	}

	async leaveGroup(groupUuid: string): Promise<void> {
		return this.api
			.processRequest("POST", `/groups/${groupUuid}/leave`)
			.then((res) => res.data);
	}

	async searchUserByEmail(email: string): Promise<User | null> {
		try {
			return await this.api
				.processRequest(
					"GET",
					`/auth/search/email/${encodeURIComponent(email)}`
				)
				.then((res) => res.data);
		} catch {
			return null;
		}
	}
}
